import {
  BillDetailed,
  BillSponsor,
  BillSubjectMention,
  BillVersion,
} from '@enview/interface/types/bills/Bill';
import { translateBillStatus } from '@enview/interface/utils/Language';
import { faCaretDown, faCaretLeft, faListAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Can from '../../components/Can';
import Hideable from '../../components/Hideable';
import { g as abilityGlossary } from '../../config/ability';
import getJurisdictionAdapter from '../../helpers/jurisdictions';
import { desktopMediaQuery } from '../../helpers/mediaQueries';
import NavigationTab, { TabComponent } from '../Shared/NavigationTabs/NavigationTab';
import '../Shared/NavigationTabs/NavigationTab.scss';
import BillActivityFeed from './BillActivityFeed';
import { Trans, useTranslation } from 'react-i18next';
import { UserActionAPI } from '../../api';
import BillRelationships from '../../components/bills/BillRelationships';
import {
  checkPermission,
  getTeamMode,
  setBillSidebarCurrentTab,
  setUserActionCardRefs,
} from '../../dux';
import { State, Thunk } from '../../dux/@types';
import { BillSidebarTabs, getBillAlias, formatDate } from '../../helpers/BillHelper';
import ValueBadge from '../Shared/Badges/ValueBadge';
import GateTooltip, { GateTooltipFeature } from '../Shared/Tooltips/TooltipGate';
import BillAiFeatures from './BillAiFeatures';
import BillViewTitle from './BillViewTitle';
import SimilarBills from './SimilarBills';
import { partyString } from '../../utils';
import moment from 'moment';

const DESKTOP_TABS = [
  BillSidebarTabs.DETAILS,
  BillSidebarTabs.INTELLIGENCE,
  BillSidebarTabs.ACTIVITY,
];

type BillSidebarProps = {
  bill: BillDetailed;
  billVersion?: BillVersion;
  billRender: JSX.Element;
  selectedBillVersion?: number;
  setSelectedBillVersion: (version: number) => void;
};

const BillSidebar = (props: BillSidebarProps): ReactElement => {
  const { bill, billVersion, billRender, selectedBillVersion, setSelectedBillVersion } =
    props;

  const { t } = useTranslation();
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const teamModeId = useSelector(getTeamMode);
  const currentTab = useSelector(
    (state: State) => state.billView.billSidebarCurrentTab,
  );
  const [isDesktopView, setIsDesktopView] = useState(desktopMediaQuery.matches);
  const [billSubjectsExpanded, setBillSubjectsExpanded] = useState(false);

  const params = useMemo(
    () => ({
      teamId: teamModeId,
      includeBillDetails: false,
      billId: bill.id,
    }),
    [teamModeId, bill.id],
  );
  const { data, error } = UserActionAPI.endpoints.getUserActions.useQuery(params);

  const dispatch = useDispatch();
  const billAlias = getBillAlias(bill.userAliases, teamModeId);
  const showDetails = bill && (data !== undefined || !orgUser);

  const canViewCommittee = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.COMMITTEE, true),
    );
  };

  const canViewIntelligence = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.BILL_INTELLIGENCE, true),
    );
  };

  const handleResize = (): void => {
    setIsDesktopView(desktopMediaQuery.matches);
  };

  const handleActivityLink = (e: React.SyntheticEvent) => {
    e.preventDefault();

    dispatch(setBillSidebarCurrentTab(BillSidebarTabs.ACTIVITY));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isDesktopView && !currentTab)
      dispatch(setBillSidebarCurrentTab(BillSidebarTabs.DETAILS));
  }, [dispatch, isDesktopView, currentTab]);

  useEffect(() => {
    if (!isDesktopView) dispatch(setBillSidebarCurrentTab());
  }, [dispatch, isDesktopView, bill.id]);

  useEffect(() => {
    if (error) {
      return;
    }
    dispatch(setUserActionCardRefs(data));
  }, [dispatch, error, data]);

  const renderSponsorList = (sponsorList: BillSponsor[]): ReactElement => {
    return (
      <div>
        {sponsorList.map((sponsor) => {
          if (sponsor.person?.id) {
            return (
              <div key={sponsor.id}>
                <Link to={`/person/${sponsor.person.id}`}>
                  {`${sponsor.name}${partyString(sponsor)}`}
                </Link>
              </div>
            );
          }
          return <div key={sponsor.id}>{sponsor.name}</div>;
        })}
      </div>
    );
  };

  const renderSponsors = (): ReactElement | undefined => {
    const { sponsors } = bill;
    if (!sponsors) return undefined;
    const currentSponsors = sponsors.filter((s) => !s.isRemoved);
    const primarySponsors = currentSponsors.filter((s) => s.isPrimary);
    const hasPrimarySponsor = primarySponsors.length > 0;
    const coSponsors = currentSponsors.filter((s) => !s.isPrimary && !s.isRemoved);
    const hasCosponsor = coSponsors.length > 0;

    return (
      <>
        {hasPrimarySponsor && (
          <div className="detail-item">
            <h3 className="title">
              {`Primary Author${primarySponsors.length > 1 ? 's' : ''}`}
            </h3>
            {renderSponsorList(primarySponsors)}
          </div>
        )}
        {hasCosponsor && (
          <div className="detail-item">
            <h3 className="title">{`Co-sponsor${coSponsors.length > 1 ? 's' : ''}`}</h3>
            {renderSponsorList(coSponsors)}
          </div>
        )}
      </>
    );
  };

  const renderCommittees = (): ReactElement => {
    const { committeeMentions } = bill;
    return (
      <>
        (
        {committeeMentions.map((committeeMention) => {
          if (committeeMention.committee?.id) {
            return (
              <span
                className="committee-list"
                id={`committees-${committeeMention.id}`}
                key={`committees-${committeeMention.id}`}
              >
                <Link
                  aria-disabled={!canViewCommittee()}
                  to={
                    canViewCommittee()
                      ? `/committee/${committeeMention.committee.id}`
                      : '#'
                  }
                >
                  {committeeMention.committee.name}
                </Link>
                {!canViewCommittee() && (
                  <GateTooltip
                    accountType="pro"
                    anchorId={`committees-${committeeMention.id}`}
                    customContent={
                      <Trans
                        components={{
                          committeeLink: (
                            <a href={t('urls.legislatorDetails')} target="_blank" />
                          ),
                          gate: <p className="tooltip-header" />,
                        }}
                        i18nKey="featureGating.tooltipText.committeesInfo"
                        key="featureGating.tooltipText.committeesInfo"
                      />
                    }
                    featureName={GateTooltipFeature.CommitteeMentions}
                    place="bottom"
                  />
                )}
              </span>
            );
          }
          if (committeeMention.name) {
            return (
              <span
                className="committee-list"
                key={`committees-${committeeMention.id}`}
              >
                {committeeMention.name}
              </span>
            );
          }
          return undefined;
        })}
        )
      </>
    );
  };

  const renderSubject = (subject: BillSubjectMention): ReactElement => {
    return (
      <div key={subject.billSubjectId}>
        {subject.billSubject && subject.billSubject.displayName}
      </div>
    );
  };

  const renderSubjects = (billSubjects: BillSubjectMention[]): ReactElement => {
    let subjectsHead = billSubjects;
    let subjectsTail: BillSubjectMention[] = [];
    const isSplit = subjectsHead.length > 3;
    if (isSplit) {
      subjectsHead = billSubjects.slice(0, 3);
      subjectsTail = billSubjects.slice(3);
    }
    return (
      <div className="detail-item">
        <h3 className="title">{t('bill.sidebar.sourceBillTopics.header')}</h3>
        {subjectsHead.map(renderSubject)}
        {isSplit && billSubjectsExpanded && subjectsTail.map(renderSubject)}
        {isSplit && (
          <button
            className="btn-link btn-view-more"
            onClick={() => setBillSubjectsExpanded(!billSubjectsExpanded)}
            type="button"
          >
            {billSubjectsExpanded ? 'View less' : 'View more'}
          </button>
        )}
      </div>
    );
  };

  // Bill Details
  const sponsorsView = bill.sponsors && bill.sponsors.length > 0 && renderSponsors();

  const billStatus: string = translateBillStatus(
    bill.status,
    bill.session.state.abbreviation,
  );
  const billCommittee =
    (bill.committeeMentions &&
      bill.committeeMentions.length > 0 &&
      renderCommittees()) ||
    '(no committee)';

  const billDetailsTitle = (
    <h2 className="react-sanfona-item-title">
      <div className="icon">
        <FontAwesomeIcon icon={faListAlt} size="sm" />
      </div>
      <span>Bill Details</span>
      <FontAwesomeIcon icon={faCaretDown} size="lg" />
    </h2>
  );

  const jurisdictionAdapter = getJurisdictionAdapter(bill.session.state.abbreviation);
  const billIntroducedAction = jurisdictionAdapter.findIntroducedAction(bill.actions);

  const renderLastUpdatedInfo = (): ReactElement => {
    let label;
    let date;

    const actions = bill.actions
      .filter((a) => a.actionDate?.length > 0)
      .sort((a, b) => a.actionDate.localeCompare(b.actionDate))
      .reverse();

    // Prefer latest action date if all actions have dates
    if (actions.length > 0 && actions.length === bill.actions.length) {
      label = 'Latest Action';
      date = formatDate(actions[0].actionDate, 'short');
    } else {
      label = 'Created on';
      date = moment.utc(bill.createdAt).format('MM/DD/YYYY');
    }
    return (
      <div className="detail-item">
        <h3 className="title">{label}</h3>
        <p className="activity-link" onClick={handleActivityLink}>
          {showDetails ? date : null}
        </p>
      </div>
    );
  };
  const lastUpdatedInfo = renderLastUpdatedInfo();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const billDetails = (
    <div className="bill-details">
      <div className="detail-item">
        <h3 className="title">Bill Title</h3>
        {showDetails ? bill.name : null}
      </div>
      <BillAiFeatures bill={bill} />
      {bill.summary && (
        <div className="detail-item">
          <h3 className="title">Bill Summary</h3>
          {showDetails ? bill.summary : null}
        </div>
      )}
      {bill.billSubjectMentions &&
        bill.billSubjectMentions.length > 0 &&
        renderSubjects(bill.billSubjectMentions)}

      {sponsorsView}

      <div className="detail-item">
        <h3 className="title">Bill Status</h3>
        {showDetails && (
          <div className="bill-status">
            {' '}
            {billStatus},{billCommittee}{' '}
          </div>
        )}
      </div>
      {lastUpdatedInfo}
      {billIntroducedAction && billIntroducedAction.actionDate && (
        <div className="detail-item">
          <h3 className="title">Introduced</h3>
          <p className="activity-link" onClick={handleActivityLink}>
            {showDetails ? formatDate(billIntroducedAction.actionDate, 'short') : null}
          </p>
        </div>
      )}
    </div>
  );

  const renderBillDetails = (): ReactElement | undefined => (
    <div className="bill-sidebar-content">
      <Hideable expanded title={billDetailsTitle}>
        {billDetails}
      </Hideable>
    </div>
  );

  const renderIntelligence = (): ReactElement | undefined => (
    <div>
      <div className="bill-sidebar-content">
        <BillRelationships
          billDetailNumber={bill.number}
          billVersions={bill.versions}
          identicalBills={bill.identical}
          relatedBills={bill.related}
        />
        <SimilarBills
          bill={bill}
          versionId={billVersion ? billVersion.id : undefined}
        />
      </div>
    </div>
  );

  const renderActivityTab = (): ReactElement | undefined => (
    <BillActivityFeed
      bill={bill}
      billVersionDisplayed={billVersion}
      orgUser={orgUser}
      selectedBillVersion={selectedBillVersion}
      setSelectedBillVersion={setSelectedBillVersion}
      userActions={data || []}
    />
  );

  const getTabs = (): (string | TabComponent)[] => {
    const allSimilarBillCount =
      (bill.identical?.length ?? 0) + (bill.related?.length ?? 0);
    const tabsWithComponents = DESKTOP_TABS.map((tab) => {
      if (tab === BillSidebarTabs.INTELLIGENCE)
        return {
          name: tab,
          component: (
            <div>
              RELATED BILLS{' '}
              {allSimilarBillCount > 0 && canViewIntelligence() && (
                <ValueBadge value={allSimilarBillCount} />
              )}
            </div>
          ),
        };
      return tab;
    });
    return isDesktopView ? tabsWithComponents : Object.values(BillSidebarTabs);
  };

  return (
    <div className="bill-sidebar sidebar-sticky">
      <div className="back-to-search">
        <Can I={abilityGlossary.VIEW} a={abilityGlossary.BILL_SEARCH} passThrough>
          {(can: boolean) => (
            <button className="btn btn-link help-link" disabled={!can} type="button">
              <Link className="breadcrumb-link" to="/legislative-tracking">
                <FontAwesomeIcon color="000" icon={faCaretLeft} size="sm" />
                <span>Back to search</span>
              </Link>
            </button>
          )}
        </Can>
      </div>
      <NavigationTab
        currentTab={currentTab || BillSidebarTabs.BILL_TEXT}
        setCurrentTab={(tab) => {
          if (tab === BillSidebarTabs.INTELLIGENCE && !canViewIntelligence())
            dispatch(setBillSidebarCurrentTab(currentTab));
          else dispatch(setBillSidebarCurrentTab(tab));
        }}
        tabs={getTabs()}
      >
        <div className="title-mobile">
          <BillViewTitle bill={bill} billAlias={billAlias} />
        </div>
        <div className="nav-tab">
          {!isDesktopView &&
            (!currentTab || currentTab === BillSidebarTabs.BILL_TEXT) &&
            billRender}

          {((isDesktopView && !currentTab) || currentTab === BillSidebarTabs.DETAILS) &&
            renderBillDetails()}

          {canViewIntelligence() &&
            currentTab === BillSidebarTabs.INTELLIGENCE &&
            renderIntelligence()}

          {currentTab === BillSidebarTabs.ACTIVITY && renderActivityTab()}
        </div>
        {!canViewIntelligence() && (
          <GateTooltip
            accountType="pro"
            anchorId="nav-intelligence"
            customContent={
              <Trans
                components={{
                  intelligenceLink: (
                    <a href={t('urls.globalBillSearch')} target="_blank" />
                  ),
                  gate: <p className="tooltip-header" />,
                }}
                i18nKey="featureGating.tooltipText.intelligenceTab"
                key="featureGating.tooltipText.intelligenceTab"
              />
            }
            featureName={GateTooltipFeature.Intelligence}
            place="top"
          />
        )}
      </NavigationTab>
    </div>
  );
};

export default BillSidebar;
